import { AxiosResponse, AxiosRequestConfig } from 'axios';
import PhfExposure from '@utils/phf/PhfExposure';
import { reportCustomError } from '../../../owlReporter'; // eslint-disable-line import/no-cycle

/**
 * 业务状态码非0白名单配置
 *
 * codeListToIgnore 和 msgRegexp 命中任意一个表示命中白名单
 */
type WhiteListEntry = {
    /** 要加白的url */
    urlPath: string;
    /** 需要加白的 code 列表 */
    codeListToIgnore?: Array<number | string>;
    /** 需要加白的特定msg正则列表 */
    msgRegexpToIgnroe?: RegExp[];
};

const whiteList: WhiteListEntry[] = [
    {
        urlPath: '/v2/epassport/logon',
        /**
         * 1022: 需绑定手机
         * 1026，1027: 跳转指定url
         * 1021: 新设备需验证手机号
         * 1024: 账号下无门店，跳转自入驻
         */
        codeListToIgnore: [1022, 1026, 1027, 1021, 1024],
        msgRegexpToIgnroe: [/账号内外卖门店均已下线/],
    },
    {
        urlPath: '/v2/shop/distributionSettings/r/isNeedAudit',
        msgRegexpToIgnroe: [/配送范围超过限定距离/],
    },
    {
        urlPath: '/v2/order/receive/unprocessed/w/confirm',
        /**
         * 4011: 订单已经被处理，再次调用确认接单。
         */
        codeListToIgnore: [4011],
    },
    {
        urlPath: '/horn_config',
    },
    {
        urlPath: 'https://waimaie.meituan.com/horn_config',
    },
    {
        urlPath: '/static/docs/defaultPrintTemplate.xml',
    },
];

/**
 * 对 Ajax 请求拦截，对异常业务状态码进行上报
 * @param response 请求返回的response
 */
const onFulfilled = (response: AxiosResponse) => {
    if (response.data && response.data.code !== 0 && response.data.code !== '0') {
        // 是否命中白名单
        let skipReprot = false;
        const currentUrlPath = (response.config.url || '').split('?')[0];

        whiteList.forEach(entry => {
            if (entry.urlPath === currentUrlPath) {
                if (
                    Array.isArray(entry.codeListToIgnore) &&
                    entry.codeListToIgnore.indexOf(response.data.code) !== -1
                ) {
                    skipReprot = true;
                }

                if (
                    typeof response.data.msg === 'string' &&
                    Array.isArray(entry.msgRegexpToIgnroe) &&
                    entry.msgRegexpToIgnroe.filter(regexp => response.data.msg.match(regexp))
                        .length > 0
                ) {
                    skipReprot = true;
                }

                if (!entry.codeListToIgnore && !entry.msgRegexpToIgnroe) {
                    // 根据URL直接忽略
                    skipReprot = true;
                }
            }
        });

        if (currentUrlPath.includes('printtemplate.waimai.meituan.com')) {
            // 小票模板是XML文件，无需检查code
            skipReprot = true;
        }

        // 如果是拼好饭，则跳过上报
        if (/^\/gw\/phf\//.test(currentUrlPath)) {
            skipReprot = true;
            try {
                const Exposure = PhfExposure.getInstance();
                let duration = 0;
                const config: AxiosRequestConfig & { meta?: { requestStartedAt?: number } } =
                    response.config;
                if (config && config.meta && config.meta.requestStartedAt) {
                    duration = Date.now() - config.meta.requestStartedAt;
                }
                // 异常上报
                Exposure.reportPhfCardError({
                    name: `Code: ${response.data.code}`,
                    message: `API: ${(response.config.url || '').replace(/\?.*$/, '')},  MSG: ${
                        response.data.msg
                    }`,
                    category: 'AJAX_BUSINESS_CODE_NONE_ZERO',
                    tags: {
                        fullPath: response.config.url || '',
                    },
                    level: 'WARN',
                });
                // 业务状态更新，这里会重复上报（只是，为了区别出异常业务 code）
                Exposure.reportApi({
                    name: response.request?.responseURL,
                    networkCode: response.status,
                    statusCode: response.data?.code,
                    responseTime: duration,
                });
            } catch (e) {
                console.log('拼好饭业务状态码上报异常');
            }
        }

        if (!skipReprot) {
            reportCustomError({
                name: `Code: ${response.data.code}`,
                message: `API: ${(response.config.url || '').replace(/\?.*$/, '')},  MSG: ${
                    response.data.msg
                }`,
                category: 'AJAX_BUSINESS_CODE_NONE_ZERO',
                tags: {
                    fullPath: response.config.url || '',
                },
                level: 'WARN',
            });
        }
    }

    if (!response.data) {
        // 响应体是 undefined, 0, "", null 等
        reportCustomError({
            name: `Code: 空body`,
            message: `API: ${(response.config.url || '').replace(/\?.*$/, '')},  BODY: ${
                response.data
            }`,
            category: 'AJAX_BUSINESS_CODE_NONE_ZERO',
            tags: {
                fullPath: response.config.url || '',
            },
            level: 'WARN',
        });
    }

    return response;
};

export default {
    onFulfilled,
};
